import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, Link2, Edit2, Trash2, Download, ExternalLink,
  X, Search, LayoutGrid, LayoutList, RefreshCw, Plus,
  ShieldCheck, Tag, Monitor, Package, FileType,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  designTemplatesService,
  DESIGN_CATEGORIES,
  FORMAT_CATEGORY_META,
} from '../api/designTemplatesApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

// ── Helpers ───────────────────────────────────────────────────────────
const fmt = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
};
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const FormatBadge = ({ category }) => {
  const meta = FORMAT_CATEGORY_META[category?.toLowerCase()] || FORMAT_CATEGORY_META.other;
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
          style={{ backgroundColor: meta.bg, color: meta.color }}>
      {meta.label}
    </span>
  );
};

const CategoryBadge = ({ category }) => (
  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs capitalize"
        style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
    {(category || '').replace(/_/g, ' ')}
  </span>
);

// ── Link Modal ────────────────────────────────────────────────────────
const LinkModal = ({ isOpen, onClose, onSave }) => {
  const empty = {
    name: '', description: '', category: '', purpose: '',
    fileUrl: '', thumbnailUrl: '', sourceUrl: '',
    targetPlatforms: [], tags: [], previewUrls: [],
    brandCompliant: false, isInspiration: false, license: '',
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const csv = (v) => v.split(',').map(s => s.trim()).filter(Boolean);

  useEffect(() => { if (isOpen) setForm(empty); }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); onClose(); }
    finally { setSaving(false); }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-lg rounded-2xl border p-6 max-h-[92vh] overflow-y-auto"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Link External Asset</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Asset URL *</label>
            <input required type="url" value={form.fileUrl} onChange={e => set('fileUrl', e.target.value)}
              placeholder="https://figma.com/file/…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
            <input required type="text" value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="Display name…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              rows={2} className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                <option value="">Select…</option>
                {DESIGN_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">License</label>
              <input type="text" value={form.license} onChange={e => set('license', e.target.value)}
                placeholder="MIT, CC-BY, proprietary…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Thumbnail URL</label>
            <input type="url" value={form.thumbnailUrl} onChange={e => set('thumbnailUrl', e.target.value)}
              placeholder="https://…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tags</label>
            <input type="text" value={form.tags.join(', ')} onChange={e => set('tags', csv(e.target.value))}
              placeholder="Comma-separated tags…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Target Platforms</label>
            <input type="text" value={form.targetPlatforms.join(', ')}
              onChange={e => set('targetPlatforms', csv(e.target.value))}
              placeholder="instagram, web, print…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.brandCompliant} onChange={e => set('brandCompliant', e.target.checked)} />
              <span style={{ color: SYSTEM_COLORS.textMuted }}>Brand compliant</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.isInspiration} onChange={e => set('isInspiration', e.target.checked)} />
              <span style={{ color: SYSTEM_COLORS.textMuted }}>Inspiration only</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm"
              style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Linking…' : 'Link Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Upload Modal ──────────────────────────────────────────────────────
const UploadModal = ({ isOpen, onClose, onSave }) => {
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({
    name: '', description: '', category: '', purpose: '',
    tags: [], targetPlatforms: [],
    brandCompliant: false, isInspiration: false, thumbnailUrl: '',
  });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const setM = (k, v) => setMeta(m => ({ ...m, [k]: v }));
  const csv = (v) => v.split(',').map(s => s.trim()).filter(Boolean);

  useEffect(() => {
    if (isOpen) { setFile(null); setMeta({ name: '', description: '', category: '', purpose: '', tags: [], targetPlatforms: [], brandCompliant: false, isInspiration: false, thumbnailUrl: '' }); }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); if (!meta.name) setM('name', f.name.replace(/\.[^.]+$/, '')); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Select a file first'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', meta.name || file.name);
      if (meta.description)     fd.append('description', meta.description);
      if (meta.category)        fd.append('category', meta.category);
      if (meta.purpose)         fd.append('purpose', meta.purpose);
      if (meta.thumbnailUrl)    fd.append('thumbnail_url', meta.thumbnailUrl);
      fd.append('brand_compliant', meta.brandCompliant);
      fd.append('is_inspiration', meta.isInspiration);
      meta.tags.forEach(t => fd.append('tags', t));
      meta.targetPlatforms.forEach(p => fd.append('target_platforms', p));
      await onSave(fd);
      onClose();
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-lg rounded-2xl border p-6 max-h-[92vh] overflow-y-auto"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Upload Design File</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Drop zone */}
          <div
            onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 cursor-pointer transition-colors hover:border-blue-400 hover:bg-blue-50"
            style={{ borderColor: file ? BRAND_COLORS.primary : SYSTEM_COLORS.borderSoft }}>
            <input ref={fileRef} type="file" className="hidden" onChange={handleFileChange}
              accept=".psd,.ai,.indd,.xd,.aep,.prproj,.fig,.sketch,.afdesign,.afpub,.afphoto,.procreate,.cdr,.clip,.kra,.png,.jpg,.jpeg,.gif,.webp,.heic,.avif,.tiff,.svg,.eps,.pdf,.mp4,.mov,.webm,.json,.blend,.fbx,.obj,.stl,.glb,.gltf,.ttf,.otf,.woff,.woff2,.zip,.rar,.7z" />
            <Upload size={24} style={{ color: file ? BRAND_COLORS.primary : SYSTEM_COLORS.textSoft }} />
            {file ? (
              <div className="text-center">
                <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.primary }}>{file.name}</p>
                <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{fmt(file.size)}</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>Click to choose a file</p>
                <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                  PSD, AI, Figma, Sketch, SVG, PNG, MP4 and more · Max 500 MB
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Display Name</label>
            <input type="text" value={meta.name} onChange={e => setM('name', e.target.value)}
              placeholder="Defaults to filename…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea value={meta.description} onChange={e => setM('description', e.target.value)}
              rows={2} className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select value={meta.category} onChange={e => setM('category', e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                <option value="">Select…</option>
                {DESIGN_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Thumbnail URL</label>
              <input type="url" value={meta.thumbnailUrl} onChange={e => setM('thumbnailUrl', e.target.value)}
                placeholder="https://…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tags</label>
            <input type="text" value={meta.tags.join(', ')} onChange={e => setM('tags', csv(e.target.value))}
              placeholder="Comma-separated tags…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Target Platforms</label>
            <input type="text" value={meta.targetPlatforms.join(', ')}
              onChange={e => setM('targetPlatforms', csv(e.target.value))}
              placeholder="instagram, web, print…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={meta.brandCompliant} onChange={e => setM('brandCompliant', e.target.checked)} />
              <span style={{ color: SYSTEM_COLORS.textMuted }}>Brand compliant</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={meta.isInspiration} onChange={e => setM('isInspiration', e.target.checked)} />
              <span style={{ color: SYSTEM_COLORS.textMuted }}>Inspiration only</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm"
              style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving || !file}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Edit Metadata Modal ───────────────────────────────────────────────
const EditModal = ({ isOpen, onClose, onSave, initial }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const csv = (v) => v.split(',').map(s => s.trim()).filter(Boolean);

  useEffect(() => {
    if (initial) setForm({
      name: initial.name || '',
      description: initial.description || '',
      category: initial.category || '',
      purpose: initial.purpose || '',
      thumbnailUrl: initial.thumbnailUrl || '',
      sourceUrl: initial.sourceUrl || '',
      license: initial.license || '',
      tags: initial.tags || [],
      targetPlatforms: initial.targetPlatforms || [],
      brandCompliant: initial.brandCompliant || false,
      isInspiration: initial.isInspiration || false,
    });
  }, [initial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); onClose(); }
    finally { setSaving(false); }
  };

  if (!isOpen || !initial) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-lg rounded-2xl border p-6 max-h-[92vh] overflow-y-auto"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Edit Metadata</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
            <input required type="text" value={form.name || ''} onChange={e => set('name', e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea value={form.description || ''} onChange={e => set('description', e.target.value)}
              rows={2} className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select value={form.category || ''} onChange={e => set('category', e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                <option value="">Select…</option>
                {DESIGN_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">License</label>
              <input type="text" value={form.license || ''} onChange={e => set('license', e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tags</label>
            <input type="text" value={(form.tags || []).join(', ')} onChange={e => set('tags', csv(e.target.value))}
              placeholder="Comma-separated tags…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Target Platforms</label>
            <input type="text" value={(form.targetPlatforms || []).join(', ')}
              onChange={e => set('targetPlatforms', csv(e.target.value))}
              placeholder="instagram, web, print…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Thumbnail URL</label>
            <input type="url" value={form.thumbnailUrl || ''} onChange={e => set('thumbnailUrl', e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.brandCompliant} onChange={e => set('brandCompliant', e.target.checked)} />
              <span style={{ color: SYSTEM_COLORS.textMuted }}>Brand compliant</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.isInspiration} onChange={e => set('isInspiration', e.target.checked)} />
              <span style={{ color: SYSTEM_COLORS.textMuted }}>Inspiration only</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm"
              style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Template Card ─────────────────────────────────────────────────────
const TemplateCard = ({ template: t, onEdit, onDelete, onDownload, view }) => {
  const isGrid = view === 'grid';

  if (!isGrid) {
    return (
      <div className="flex items-center gap-4 px-4 py-3 border-b last:border-0 hover:bg-gray-50 transition-colors"
           style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        {/* Thumb */}
        <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          {t.thumbnailUrl
            ? <img src={t.thumbnailUrl} alt={t.name} className="object-cover w-full h-full"
                   onError={e => { e.target.style.display = 'none'; }} />
            : <FileType size={20} style={{ color: SYSTEM_COLORS.textSoft }} />}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <p className="text-sm font-semibold truncate" style={{ color: NEUTRAL_COLORS.black }}>{t.name}</p>
            {t.brandCompliant && <ShieldCheck size={13} className="text-green-500 flex-shrink-0" title="Brand compliant" />}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {t.category && <CategoryBadge category={t.category} />}
            {t.fileFormatCategory && <FormatBadge category={t.fileFormatCategory} />}
            <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>
              {t.fileExtension?.toUpperCase()} · {fmt(t.fileSize)}
            </span>
          </div>
        </div>

        {/* Meta */}
        <div className="hidden md:flex items-center gap-3 text-xs flex-shrink-0" style={{ color: SYSTEM_COLORS.textMuted }}>
          <span className="flex items-center gap-1"><Download size={11} /> {t.downloadCount || 0}</span>
          <span>{fmtDate(t.updatedAt)}</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {t.fileUrl && (
            <a href={t.fileUrl} target="_blank" rel="noopener noreferrer" onClick={() => onDownload(t)}
               className="p-1.5 text-gray-400 hover:text-blue-600 rounded">
              <ExternalLink size={14} />
            </a>
          )}
          <button onClick={() => onEdit(t)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded">
            <Edit2 size={14} />
          </button>
          <button onClick={() => onDelete(t)} className="p-1.5 text-gray-400 hover:text-red-600 rounded">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border overflow-hidden flex flex-col group"
         style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
      {/* Thumbnail */}
      <div className="relative h-40 bg-gray-100 flex items-center justify-center overflow-hidden">
        {t.thumbnailUrl
          ? <img src={t.thumbnailUrl} alt={t.name} className="w-full h-full object-cover"
                 onError={e => { e.target.style.display = 'none'; }} />
          : <FileType size={36} style={{ color: SYSTEM_COLORS.textSoft }} />}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          {t.fileUrl && (
            <a href={t.fileUrl} target="_blank" rel="noopener noreferrer" onClick={() => onDownload(t)}
               className="p-2 rounded-full bg-white shadow text-blue-600 hover:text-blue-700">
              <ExternalLink size={14} />
            </a>
          )}
          <button onClick={() => onEdit(t)} className="p-2 rounded-full bg-white shadow text-gray-600 hover:text-blue-600">
            <Edit2 size={14} />
          </button>
          <button onClick={() => onDelete(t)} className="p-2 rounded-full bg-white shadow text-gray-600 hover:text-red-600">
            <Trash2 size={14} />
          </button>
        </div>

        {/* Brand badge */}
        {t.brandCompliant && (
          <div className="absolute top-2 right-2 rounded-full bg-green-100 p-1" title="Brand compliant">
            <ShieldCheck size={12} className="text-green-600" />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-3 flex-1 space-y-2">
        <p className="text-sm font-semibold leading-snug line-clamp-2" style={{ color: NEUTRAL_COLORS.black }}>
          {t.name}
        </p>
        {t.description && (
          <p className="text-xs line-clamp-2" style={{ color: SYSTEM_COLORS.textMuted }}>{t.description}</p>
        )}
        <div className="flex flex-wrap gap-1">
          {t.category && <CategoryBadge category={t.category} />}
          {t.fileFormatCategory && <FormatBadge category={t.fileFormatCategory} />}
        </div>

        {(t.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {t.tags.slice(0, 3).map(tag => (
              <span key={tag} className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs"
                    style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
                <Tag size={9} /> {tag}
              </span>
            ))}
            {t.tags.length > 3 && (
              <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>+{t.tags.length - 3}</span>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-3 py-2 border-t text-xs"
           style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}>
        <span className="flex items-center gap-1">
          <FileType size={10} /> {t.fileExtension?.toUpperCase() || '—'} · {fmt(t.fileSize)}
        </span>
        <span className="flex items-center gap-1">
          <Download size={10} /> {t.downloadCount || 0}
        </span>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────
const DesignTemplateLibrary = ({ filterInspiration }) => {
  const [templates, setTemplates]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [view, setView]               = useState('grid');
  const [search, setSearch]           = useState('');
  const [catFilter, setCatFilter]     = useState('');
  const [tagFilter, setTagFilter]     = useState('');
  const [showUpload, setShowUpload]   = useState(false);
  const [showLink, setShowLink]       = useState(false);
  const [editTarget, setEditTarget]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (catFilter) filters.category = catFilter;
      if (tagFilter) filters.tag = tagFilter;
      if (filterInspiration !== undefined) filters.isInspiration = filterInspiration;
      const data = await designTemplatesService.list(filters);
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load asset library');
    } finally {
      setLoading(false);
    }
  }, [catFilter, tagFilter, filterInspiration]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (formData) => {
    try {
      const created = await designTemplatesService.upload(formData);
      setTemplates(prev => [created, ...prev]);
      toast.success('File uploaded');
    } catch (err) { toast.error(err?.message || 'Upload failed'); throw err; }
  };

  const handleLink = async (data) => {
    try {
      const created = await designTemplatesService.link(data);
      setTemplates(prev => [created, ...prev]);
      toast.success('Asset linked');
    } catch (err) { toast.error(err?.message || 'Link failed'); throw err; }
  };

  const handleEdit = async (updates) => {
    try {
      const updated = await designTemplatesService.update(editTarget.id, updates);
      setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
      toast.success('Updated');
    } catch (err) { toast.error(err?.message || 'Update failed'); throw err; }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete "${template.name}"?`)) return;
    try {
      await designTemplatesService.delete(template.id);
      setTemplates(prev => prev.filter(t => t.id !== template.id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownload = async (template) => {
    try { await designTemplatesService.logDownload(template.id); }
    catch { /* log silently */ }
  };

  const visible = templates.filter(t =>
    !search ||
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    (t.tags || []).some(tag => tag.toLowerCase().includes(search.toLowerCase()))
  );

  const totalDownloads = templates.reduce((s, t) => s + (t.downloadCount || 0), 0);
  const brandCount     = templates.filter(t => t.brandCompliant).length;

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Assets', value: templates.length },
          { label: 'Brand Compliant', value: brandCount },
          { label: 'Downloads', value: totalDownloads.toLocaleString() },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-xl border p-4"
               style={{ backgroundColor: SYSTEM_COLORS.primaryTint, borderColor: SYSTEM_COLORS.borderSoft }}>
            <p className="text-xs mb-1" style={{ color: BRAND_COLORS.primary }}>{label}</p>
            <p className="text-xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        {/* Search + filters */}
        <div className="flex flex-1 gap-2 flex-wrap">
          <div className="flex flex-1 min-w-0 items-center gap-2 rounded-xl border px-3 py-2"
               style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
            <Search size={14} style={{ color: SYSTEM_COLORS.textSoft, flexShrink: 0 }} />
            <input type="text" placeholder="Search by name or tag…" value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 text-sm bg-transparent outline-none min-w-0"
              style={{ color: NEUTRAL_COLORS.black }} />
          </div>

          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
            className="rounded-xl border px-3 py-2 text-sm flex-shrink-0"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface, color: NEUTRAL_COLORS.black }}>
            <option value="">All Categories</option>
            {DESIGN_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
          </select>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* View toggle */}
          <div className="flex items-center gap-0.5 rounded-lg border p-1"
               style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            {[{ id: 'grid', icon: LayoutGrid }, { id: 'list', icon: LayoutList }].map(({ id, icon: Icon }) => (
              <button key={id} onClick={() => setView(id)}
                className="rounded-md p-1.5 transition-colors"
                style={{
                  backgroundColor: view === id ? SYSTEM_COLORS.primaryTint : 'transparent',
                  color: view === id ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                }}>
                <Icon size={16} />
              </button>
            ))}
          </div>

          <button onClick={load}
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: SYSTEM_COLORS.border, color: SYSTEM_COLORS.textMuted }}>
            <RefreshCw size={14} />
          </button>

          <button onClick={() => setShowLink(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium"
            style={{ borderColor: SYSTEM_COLORS.border, color: SYSTEM_COLORS.textMuted, backgroundColor: SYSTEM_COLORS.shellSurface }}>
            <Link2 size={14} /> Link
          </button>

          <button onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            <Upload size={14} /> Upload
          </button>
        </div>
      </div>

      {/* Category pills */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {['', ...DESIGN_CATEGORIES].map(c => (
          <button key={c || 'all'} onClick={() => setCatFilter(c)}
            className="flex-shrink-0 rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors"
            style={{
              borderColor: catFilter === c ? BRAND_COLORS.primary : SYSTEM_COLORS.borderSoft,
              backgroundColor: catFilter === c ? SYSTEM_COLORS.primaryTint : 'transparent',
              color: catFilter === c ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
            }}>
            {c ? c.replace(/_/g, ' ') : 'All'}
          </button>
        ))}
      </div>

      {/* Grid / list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2"
               style={{ borderColor: BRAND_COLORS.primary }} />
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed py-16"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <Package size={32} style={{ color: SYSTEM_COLORS.textSoft }} />
          <div className="text-center">
            <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>No assets found</p>
            <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
              Upload a file or link an external asset to get started.
            </p>
          </div>
          <div className="flex gap-2 mt-1">
            <button onClick={() => setShowLink(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}>
              <Link2 size={14} /> Link
            </button>
            <button onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              <Upload size={14} /> Upload
            </button>
          </div>
        </div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visible.map(t => (
            <TemplateCard key={t.id} template={t} view="grid"
              onEdit={(item) => setEditTarget(item)}
              onDelete={handleDelete}
              onDownload={handleDownload} />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden"
             style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
          {visible.map(t => (
            <TemplateCard key={t.id} template={t} view="list"
              onEdit={(item) => setEditTarget(item)}
              onDelete={handleDelete}
              onDownload={handleDownload} />
          ))}
        </div>
      )}

      <UploadModal isOpen={showUpload} onClose={() => setShowUpload(false)} onSave={handleUpload} />
      <LinkModal   isOpen={showLink}   onClose={() => setShowLink(false)}   onSave={handleLink} />
      <EditModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSave={handleEdit}
        initial={editTarget}
      />
    </div>
  );
};

export default DesignTemplateLibrary;
